import {
	areItemDatesValid,
	DRAGGABLE_TYPE,
	getCanvasTimelineDateFromMoment,
	ITEM_TYPE,
} from '../canvas-timeline/canvas_timeline_util';
import {ESTIMATION_UNIT, PROJECT_STATUS} from '../../../constants';
import ProjectGroupGroup from './groups/project_group_group';
import Util from '../../../forecast-app/shared/util/util';
import ProjectItem from './items/project_item';
import DateRangeItem from './items/date_range_item';
import ProjectGroup from './groups/project_group';
import LoadingGroup from './groups/loading_group';
import DateRangeGroup from './groups/date_range_group';
import {hasPermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';

const getProjectPersonsMap = data => {
	//from project id to list of project person ids, from project person id to project person
	const projectIdToProjectPersonMap = new Map(),
		projectIdToPersonIdsMap = new Map();

	const personMap = data.persons.reduce((map, currentPerson) => {
		map[currentPerson.id] = currentPerson;
		return map;
	}, {});
	for (const projectPerson of data.projectPersons) {
		const person = personMap[projectPerson.personId];
		if (!person || person.clientId || !person.active) continue;
		const projectPersonsInProject = projectIdToProjectPersonMap.has(projectPerson.projectId)
			? projectIdToProjectPersonMap.get(projectPerson.projectId)
			: [];
		projectPersonsInProject.push(projectPerson);
		projectIdToProjectPersonMap.set(projectPerson.projectId, projectPersonsInProject);
		// Popluate the projectIdToPersonIdsMap so we can find the persons who have allocations on the project, but are not on the team
		if (projectIdToPersonIdsMap.has(projectPerson.projectId)) {
			projectIdToPersonIdsMap.get(projectPerson.projectId).push(projectPerson.personId);
		} else {
			projectIdToPersonIdsMap.set(projectPerson.projectId, [projectPerson.personId]);
		}
	}

	return {
		projectIdToProjectPersonMap,
	};
};

const getClientsMap = data => {
	//from client id to client
	const clientIdToClientMap = new Map();
	for (const client of data.clients) {
		clientIdToClientMap.set(client.id, client);
	}
	return {clientIdToClientMap};
};

const sortProjectGroups = (pageComponent, groups) => {
	const projectGroups = groups.filter(group => !group.data.companyProjectGroupId);
	const runningGroups = projectGroups.filter(group => group.data.status === PROJECT_STATUS.RUNNING);
	const planningGroups = projectGroups.filter(group => group.data.status === PROJECT_STATUS.PLANNING);
	const opportunityGroups = projectGroups.filter(group => group.data.status === PROJECT_STATUS.OPPORTUNITY);
	const haltedGroups = projectGroups.filter(group => group.data.status === PROJECT_STATUS.HALTED);
	const doneGroups = projectGroups.filter(group => group.data.status === PROJECT_STATUS.DONE);
	const projectGroupGroups = groups.filter(group => group.data.companyProjectGroupId);

	const getStatusPriority = status => {
		switch (status) {
			case PROJECT_STATUS.RUNNING:
				return 0;
			case PROJECT_STATUS.PLANNING:
				return 1;
			case PROJECT_STATUS.OPPORTUNITY:
				return 2;
			case PROJECT_STATUS.HALTED:
				return 3;
			case PROJECT_STATUS.DONE:
				return 4;
			default:
				// eslint-disable-next-line no-console
				console.warn('Unrecognized project status: ' + status);
				return 50;
		}
	};

	for (const group of projectGroupGroups) {
		const projects = pageComponent.getFilterData().projects.filter(project => project.projectGroupId === group.id);
		let highestStatusPriority = 99;
		for (const project of projects) {
			const statusPriority = getStatusPriority(project.status);
			if (statusPriority < highestStatusPriority) {
				highestStatusPriority = statusPriority;
			}
		}
		switch (highestStatusPriority) {
			case 0:
				runningGroups.push(group);
				break;
			case 1:
				planningGroups.push(group);
				break;
			case 2:
				opportunityGroups.push(group);
				break;
			case 3:
				haltedGroups.push(group);
				break;
			case 4:
				doneGroups.push(group);
				break;
		}
	}
	const getProjectGroupDates = projectGroupId => {
		let startDate = null;
		let endDate = null;
		for (const project of pageComponent
			.getFilterData()
			.projects.filter(project => project.projectGroupId === projectGroupId)) {
			const projectStartDate = project.projectStartDay
				? Util.CreateNonUtcMomentDate(project.projectStartYear, project.projectStartMonth, project.projectStartDay)
				: null;
			const projectEndDate = project.projectEndDay
				? Util.CreateNonUtcMomentDate(project.projectEndYear, project.projectEndMonth, project.projectEndDay).endOf(
						'day'
				  )
				: null;
			if (projectStartDate && (!startDate || startDate.isAfter(projectStartDate))) {
				startDate = projectStartDate;
			}
			if (projectEndDate && (!endDate || endDate.isBefore(projectEndDate))) {
				endDate = projectEndDate;
			}
		}
		return {startDate, endDate};
	};
	const getProjectDates = projectId => {
		const project = pageComponent.getFilterData().projects.find(project => project.id === projectId);
		const startDate = project.projectStartDay
			? Util.CreateNonUtcMomentDate(project.projectStartYear, project.projectStartMonth, project.projectStartDay)
			: null;
		const endDate = project.projectEndDay
			? Util.CreateNonUtcMomentDate(project.projectEndYear, project.projectEndMonth, project.projectEndDay).endOf('day')
			: null;
		return {startDate, endDate};
	};

	for (const group of [runningGroups, planningGroups, haltedGroups, doneGroups]) {
		group.sort((a, b) => {
			const aDates = a.data.companyProjectGroupId ? getProjectGroupDates(a.id) : getProjectDates(a.id);
			const bDates = b.data.companyProjectGroupId ? getProjectGroupDates(b.id) : getProjectDates(b.id);
			const aHasDates = aDates.startDate && aDates.endDate;
			const bHasDates = bDates.startDate && bDates.endDate;
			if (aHasDates && !bHasDates) return -1;
			if (bHasDates && !aHasDates) return 1;
			if (!aHasDates && !bHasDates) return 0;
			if (aDates.startDate && (!bDates.startDate || aDates.startDate.isBefore(bDates.startDate))) return -1;
			if (bDates.startDate && (!aDates.startDate || bDates.startDate.isBefore(aDates.startDate))) return 1;
			return 0;
		});
	}


	const sortedGroups = runningGroups.concat(planningGroups).concat(opportunityGroups).concat(haltedGroups).concat(doneGroups);
	if (sortedGroups[0]) {
		sortedGroups[0].data.isFirstProjectGroup = true;
	}
	return sortedGroups;
};

export const createLightWeightProjectsGroupsAndItems = (pageComponent, props, data, lightWeightLoad = false, isProjectTimeline) => {
	const sharedContext = props.sharedContext;
	const {formatMessage} = props.intl;

	const {projectIdToProjectPersonMap} = getProjectPersonsMap(data);
	const {clientIdToClientMap} = getClientsMap(data);

	let groups = [];
	const items = [];

	const projectEdges = data.projects.filter(project => {
		if (hasPermission(PERMISSION_TYPE.PROJECTS_READ_ALL)) {
			return true;
		}
		if (projectIdToProjectPersonMap && projectIdToProjectPersonMap.has(project.id)) {
			for (const projectPerson of projectIdToProjectPersonMap.get(project.id)) {
				if (projectPerson.personId === data.viewer.actualPersonId) {
					return true;
				}
			}
		}
		return false;
	});
	const projectGroupTimelineGroups = new Map(),
		projectGroupTeamMap = new Map(),
		projectGroupColorMap = new Map(),
		projectGroupStartDateMap = new Map(),
		projectGroupEndDateMap = new Map(),
		projectGroupTimeLeftMap = new Map(),
		projectGroupEstimateForecastMap = new Map(),
		projectGroupHourEstMap = new Map();
	for (const projectGroup of data.projectGroups) {
		projectGroupTimelineGroups.set(
			projectGroup.id,
			new ProjectGroupGroup({
				id: projectGroup.id,
				companyProjectGroupId: projectGroup.companyProjectGroupId,
				name: projectGroup.name,
				color: projectGroup.color,
				groups: [],
				isConnectedProject: true,
				hideIfEmpty: !props.isProjectTimeline,
				drawIfAnyChildVisible: true,
				isProjectTimeline,
				onMouseEnter: sharedContext
					? undefined
					: pageComponent.onProjectGroupMouseEnter.bind(pageComponent, projectGroup),
				onMouseLeave: sharedContext
					? undefined
					: pageComponent.onProjectGroupMouseLeave.bind(pageComponent, projectGroup),
				fullDataLoaded: false,
			})
		);
		projectGroupColorMap.set(projectGroup.id, projectGroup.color);
		projectGroupTeamMap.set(projectGroup.id, []);
		projectGroupStartDateMap.set(projectGroup.id, null);
		projectGroupEndDateMap.set(projectGroup.id, null);
		projectGroupTimeLeftMap.set(projectGroup.id, 0);
		projectGroupEstimateForecastMap.set(projectGroup.id, 0);
	}
	/*
	 * map through all projects.
	 * single projects should be added to array of returned timeline groups
	 * projects that belong to project group should be added to map that later will be casted into array of timeline groups for project group
	 */
	for (const project of projectEdges) {
		let projectStartDate = null;
		let projectEndDate = null;
		const isDoneOrHalted = ['DONE', 'HALTED'].includes(project.status);
		if (project.projectStartYear && project.projectEndYear) {
			//adding project items
			projectStartDate = Util.CreateNonUtcMomentDate(
				project.projectStartYear,
				project.projectStartMonth,
				project.projectStartDay
			);
			projectEndDate = Util.CreateNonUtcMomentDate(
				project.projectEndYear,
				project.projectEndMonth,
				project.projectEndDay
			);
			const startDate = getCanvasTimelineDateFromMoment(projectStartDate);
			const endDate = getCanvasTimelineDateFromMoment(projectEndDate);
			if (areItemDatesValid(startDate, endDate)) {
				items.push(
					new ProjectItem({
						groupId: project.id,
						startDate,
						endDate,
						draggableType:
							!hasPermission(PERMISSION_TYPE.PROJECTS_UPDATE) || isDoneOrHalted || sharedContext
								? undefined
								: DRAGGABLE_TYPE.DATE_ONLY,
						color: project.projectColor,
						project,
						isHourEstimated: project.estimationUnit === ESTIMATION_UNIT.HOURS,
						isPlanning: project.status === PROJECT_STATUS.PLANNING || project.status === PROJECT_STATUS.OPPORTUNITY,
						intl: props.intl,
						planningText:
							project.status === PROJECT_STATUS.OPPORTUNITY
								? formatMessage({id: 'project_status.opportunity'})
								: formatMessage({id: 'project_status.planning'}),
						projectGroupId: project.projectGroupId,
						onMoving: sharedContext ? undefined : pageComponent.onProjectItemMoving.bind(pageComponent),
						onMoveEnd: sharedContext ? undefined : pageComponent.onProjectItemMoveEnd.bind(pageComponent),
						onMouseEnter: sharedContext
							? undefined
							: pageComponent.onDistributionItemMouseEnter.bind(
									pageComponent,
									ITEM_TYPE.PROJECT_SCHEDULING_PROJECT,
									project.projectGroupId || project.id,
									project.projectGroupId ? project.id : null
							  ),
						onMouseLeave: sharedContext
							? undefined
							: pageComponent.onDistributionItemMouseLeave.bind(pageComponent),
						onMouseMove: sharedContext ? undefined : pageComponent.onDistributionItemMouseMove.bind(pageComponent),
						onClick: sharedContext
							? undefined
							: pageComponent.onDistributionItemClick.bind(
									pageComponent,
									ITEM_TYPE.PROJECT_SCHEDULING_PROJECT,
									project.projectGroupId || project.id,
									project.projectGroupId ? project.id : null
							  ),
					})
				);
			}
			//check if project group start/end date maps need to be updated
			if (
				project.isInProjectGroup &&
				projectGroupStartDateMap.has(project.projectGroupId) &&
				projectGroupEndDateMap.has(project.projectGroupId)
			) {
				const projectGroupStartDate = projectGroupStartDateMap.get(project.projectGroupId),
					projectGroupEndDate = projectGroupEndDateMap.get(project.projectGroupId);
				if (projectGroupStartDate === null || projectStartDate.isBefore(projectGroupStartDate)) {
					projectGroupStartDateMap.set(project.projectGroupId, projectStartDate.clone());
				}
				if (projectGroupEndDate === null || projectEndDate.isAfter(projectGroupEndDate)) {
					projectGroupEndDateMap.set(project.projectGroupId, projectEndDate.clone());
				}
			}
		}
		//set timeLeft and estimateForecast maps
		if (project.isInProjectGroup) {
			projectGroupHourEstMap.set(project.projectGroupId, project.estimationUnit);
			if (projectGroupTimeLeftMap.has(project.projectGroupId)) {
				let projectGroupTimeLeft = projectGroupTimeLeftMap.get(project.projectGroupId);
				projectGroupTimeLeft += project.remaining;
				projectGroupTimeLeftMap.set(project.projectGroupId, projectGroupTimeLeft);
			}
			if (projectGroupEstimateForecastMap.has(project.projectGroupId)) {
				let projectGroupEstimateForecast = projectGroupEstimateForecastMap.get(project.projectGroupId);
				projectGroupEstimateForecast += project.forecast;
				projectGroupEstimateForecastMap.set(project.projectGroupId, projectGroupEstimateForecast);
			}
		}

		const projectSubGroups = [];

		if (project.dateRanges) {
			for (const dateRange of project.dateRanges) {
				if (dateRange.start && dateRange.end) {
					if (areItemDatesValid(dateRange.start, dateRange.end)) {
						items.push(
							new DateRangeItem({
								groupId: dateRange.id,
								startDate: dateRange.start,
								endDate: dateRange.end,
							})
						);
					}
				}
				const dateRangeGroup = new DateRangeGroup({
					id: dateRange.id,
				});
				dateRangeGroup.preventDrawing = true;
				projectSubGroups.push(dateRangeGroup);
			}
		}

		const labelIds = data.projectLabels
			.filter(projectLabel => projectLabel.projectId === project.id)
			.map(projectLabel => projectLabel.labelId);
		const projectGroup = new ProjectGroup({
			id: project.id,
			companyProjectId: project.companyProjectId,
			name: `${project.name}`,
			budgetType: project.budgetType,
			color: project.projectColor,
			status: project.status,
			clientId: project.clientId,
			clientName:
				project.clientId && clientIdToClientMap.has(project.clientId)
					? clientIdToClientMap.get(project.clientId).name
					: '',
			isInProjectGroup: project.isInProjectGroup,
			isProjectTimeline: props.isProjectTimeline,
			projectGroupColor: project.isInProjectGroup ? projectGroupColorMap.get(project.projectGroupId) : null,
			groups: projectSubGroups,
			hideIfEmpty: !props.isProjectTimeline,
			drawIfAnyChildVisible: true,
			labelIds,
			onMouseEnter: sharedContext ? undefined : pageComponent.onProjectGroupMouseEnter.bind(pageComponent, project),
			onMouseLeave: sharedContext ? undefined : pageComponent.onProjectGroupMouseLeave.bind(pageComponent, project),
			fullDataLoaded: false,
		});
		if (project.isInProjectGroup) {
			if (projectGroupTimelineGroups.has(project.projectGroupId)) {
				const projectGroupGroup = projectGroupTimelineGroups.get(project.projectGroupId);
				const groups = projectGroupGroup.groups;
				groups.push(projectGroup);
				projectGroupGroup.groups = groups;
				projectGroupTimelineGroups.set(project.projectGroupId, projectGroupGroup);
			}
		} else {
			groups.push(projectGroup);
		}
		projectGroup.groups.push(new LoadingGroup({}));
	}
	projectGroupTimelineGroups.forEach((value, key, map) => {
		//if project group is empty it means viewer was not assigned to any of its projects. Dont add these groups to the array
		if (value.groups.length !== 0) {
			if (projectGroupTeamMap.has(key)) {
				//add timeline iteam for each group
				if (projectGroupStartDateMap.get(key) !== null && projectGroupEndDateMap.get(key) !== null) {
					items.push(
						new ProjectItem({
							groupId: key,
							intl: props.intl,
							startDate: getCanvasTimelineDateFromMoment(projectGroupStartDateMap.get(key)),
							endDate: getCanvasTimelineDateFromMoment(projectGroupEndDateMap.get(key)),
							color: value.data.color,
							projects: data.projects.filter(project => project.projectGroupId === key),
							isHourEstimated: projectGroupHourEstMap.get(key) === ESTIMATION_UNIT.HOURS,
							onMouseEnter: sharedContext
								? undefined
								: pageComponent.onDistributionItemMouseEnter.bind(
										pageComponent,
										ITEM_TYPE.PROJECT_SCHEDULING_PROJECT,
										key,
										null
								  ),
							onMouseLeave: sharedContext
								? undefined
								: pageComponent.onDistributionItemMouseLeave.bind(pageComponent),
							onMouseMove: sharedContext
								? undefined
								: pageComponent.onDistributionItemMouseMove.bind(pageComponent),
							onClick: sharedContext
								? undefined
								: pageComponent.onDistributionItemClick.bind(
										pageComponent,
										ITEM_TYPE.PROJECT_SCHEDULING_PROJECT,
										key,
										null
								  ),
							useNewProgressCalculation: data.viewer.availableFeatureFlags.find(
								flag => flag.key === 'use_new_progress_calc'
							),
						})
					);
				}
			}
			groups.push(value);
		}
		if (pageComponent.isUsingNewLazyLoad) {
			value.groups.push(new LoadingGroup({}));
		}
	});
	groups = sortProjectGroups(pageComponent, groups);
	return {groups, items};
};
